import { useCallback, useEffect, useRef, useState } from "react";

const CANCEL_THRESHOLD_PX = 70;
const LOCK_THRESHOLD_PX = 60;
const MIN_RECORD_MS = 400;
const AXIS_DEADZONE_PX = 8;

export type TelegramVoiceGesturePhase =
  | "idle"
  | "holding"
  | "cancelling"
  | "locking"
  | "locked";

interface UseTelegramVoiceGestureOptions {
  disabled?: boolean;
  onStart: () => void | Promise<void>;
  onStop: () => void;
  onCancel: () => void;
}

export const useTelegramVoiceGesture = ({
  disabled = false,
  onStart,
  onStop,
  onCancel,
}: UseTelegramVoiceGestureOptions) => {
  const [phase, setPhase] = useState<TelegramVoiceGesturePhase>("idle");
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [lockProgress, setLockProgress] = useState(0);
  const [cancelProgress, setCancelProgress] = useState(0);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const phaseRef = useRef<TelegramVoiceGesturePhase>("idle");
  const startPointRef = useRef<{ x: number; y: number } | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const activePointerIdRef = useRef<number | null>(null);
  const cancelledDuringGestureRef = useRef(false);
  const lockProgressRef = useRef(0);
  const cancelProgressRef = useRef(0);
  const disabledRef = useRef(disabled);
  const onStartRef = useRef(onStart);
  const onStopRef = useRef(onStop);
  const onCancelRef = useRef(onCancel);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => {
    onStartRef.current = onStart;
  }, [onStart]);

  useEffect(() => {
    onStopRef.current = onStop;
  }, [onStop]);

  useEffect(() => {
    onCancelRef.current = onCancel;
  }, [onCancel]);

  const setPhaseBoth = useCallback((next: TelegramVoiceGesturePhase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  const resetDragVisuals = useCallback(() => {
    setDragOffsetX(0);
    setDragOffsetY(0);
    setLockProgress(0);
    setCancelProgress(0);
    lockProgressRef.current = 0;
    cancelProgressRef.current = 0;
  }, []);

  const detachDocumentListenersRef = useRef<(() => void) | null>(null);

  const detachDocumentListeners = useCallback(() => {
    detachDocumentListenersRef.current?.();
    detachDocumentListenersRef.current = null;
  }, []);

  const finishIdle = useCallback(() => {
    detachDocumentListeners();
    setPhaseBoth("idle");
    startPointRef.current = null;
    startedAtRef.current = null;
    activePointerIdRef.current = null;
    cancelledDuringGestureRef.current = false;
    resetDragVisuals();
  }, [detachDocumentListeners, resetDragVisuals, setPhaseBoth]);

  const applyPointerMove = useCallback(
    (clientX: number, clientY: number, pointerId: number) => {
      if (activePointerIdRef.current !== pointerId) return;
      if (!startPointRef.current) return;
      if (phaseRef.current === "locked" || phaseRef.current === "idle") return;
      if (cancelledDuringGestureRef.current) return;

      const deltaX = clientX - startPointRef.current.x;
      const deltaY = clientY - startPointRef.current.y;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (absX < AXIS_DEADZONE_PX && absY < AXIS_DEADZONE_PX) {
        return;
      }

      // Slide up to lock
      if (absY >= absX && deltaY < 0) {
        const progress = Math.min(1, absY / LOCK_THRESHOLD_PX);
        setDragOffsetX(0);
        setDragOffsetY(deltaY);
        setLockProgress(progress);
        setCancelProgress(0);
        lockProgressRef.current = progress;
        cancelProgressRef.current = 0;
        setPhaseBoth(progress >= 1 ? "locking" : "holding");

        if (progress >= 1) {
          setPhaseBoth("locked");
          resetDragVisuals();
          activePointerIdRef.current = null;
          startPointRef.current = null;
          detachDocumentListeners();
          try {
            buttonRef.current?.releasePointerCapture(pointerId);
          } catch {
            // ignore
          }
        }
        return;
      }

      // Slide left to cancel
      if (deltaX < 0) {
        const progress = Math.min(1, absX / CANCEL_THRESHOLD_PX);
        setDragOffsetX(deltaX);
        setDragOffsetY(0);
        setCancelProgress(progress);
        setLockProgress(0);
        cancelProgressRef.current = progress;
        lockProgressRef.current = 0;
        setPhaseBoth(progress >= 0.35 ? "cancelling" : "holding");

        if (progress >= 1) {
          cancelledDuringGestureRef.current = true;
          onCancelRef.current();
          try {
            buttonRef.current?.releasePointerCapture(pointerId);
          } catch {
            // ignore
          }
          finishIdle();
        }
        return;
      }

      // Moved right / down — keep holding, clear cancel/lock feedback
      setDragOffsetX(0);
      setDragOffsetY(0);
      setLockProgress(0);
      setCancelProgress(0);
      lockProgressRef.current = 0;
      cancelProgressRef.current = 0;
      setPhaseBoth("holding");
    },
    [detachDocumentListeners, finishIdle, resetDragVisuals, setPhaseBoth],
  );

  const applyPointerUp = useCallback(
    (pointerId: number) => {
      if (
        activePointerIdRef.current != null &&
        activePointerIdRef.current !== pointerId
      ) {
        return;
      }

      if (phaseRef.current === "locked") {
        detachDocumentListeners();
        return;
      }

      if (cancelledDuringGestureRef.current) {
        finishIdle();
        return;
      }

      if (phaseRef.current === "idle") {
        detachDocumentListeners();
        return;
      }

      try {
        buttonRef.current?.releasePointerCapture(pointerId);
      } catch {
        // ignore
      }

      const elapsed =
        startedAtRef.current != null ? Date.now() - startedAtRef.current : 0;

      if (elapsed < MIN_RECORD_MS || phaseRef.current === "cancelling") {
        onCancelRef.current();
        finishIdle();
        return;
      }

      onStopRef.current();
      finishIdle();
    },
    [detachDocumentListeners, finishIdle],
  );

  const attachDocumentListeners = useCallback(
    (pointerId: number) => {
      detachDocumentListeners();

      const onMove = (event: PointerEvent) => {
        if (event.pointerId !== pointerId) return;
        // Stop browser scroll/pan from stealing the gesture on mobile.
        if (event.cancelable) {
          event.preventDefault();
        }
        applyPointerMove(event.clientX, event.clientY, event.pointerId);
      };

      const onUp = (event: PointerEvent) => {
        if (event.pointerId !== pointerId) return;
        applyPointerUp(event.pointerId);
      };

      // iOS/Android often fire pointercancel when they start scrolling.
      // If we already blocked scroll via touch-action + touchmove, treat
      // cancel like a release when possible instead of hard-aborting mid-swipe.
      const onPointerCancel = (event: PointerEvent) => {
        if (event.pointerId !== pointerId) return;

        if (phaseRef.current === "locked") {
          detachDocumentListeners();
          return;
        }

        if (phaseRef.current === "idle") {
          detachDocumentListeners();
          return;
        }

        if (cancelledDuringGestureRef.current) {
          finishIdle();
          return;
        }

        // If user was past cancel threshold, honor cancel.
        if (cancelProgressRef.current >= 1) {
          cancelledDuringGestureRef.current = true;
          onCancelRef.current();
          finishIdle();
          return;
        }

        // If nearly locked, lock instead of aborting.
        if (lockProgressRef.current >= 0.85) {
          setPhaseBoth("locked");
          resetDragVisuals();
          activePointerIdRef.current = null;
          startPointRef.current = null;
          detachDocumentListeners();
          return;
        }

        // Otherwise behave like finger release (stop → preview).
        applyPointerUp(event.pointerId);
      };

      // Non-passive touchmove is required to preventDefault on iOS Safari.
      const onTouchMove = (event: TouchEvent) => {
        if (event.cancelable) {
          event.preventDefault();
        }
        const touch = Array.from(event.touches).find(
          (item) => item.identifier === pointerId,
        );
        // Some browsers use different touch identifier vs pointerId —
        // fall back to the first active touch while we own the gesture.
        const point = touch ?? event.touches[0];
        if (!point) return;
        applyPointerMove(point.clientX, point.clientY, pointerId);
      };

      const onTouchEnd = (event: TouchEvent) => {
        // touchend has changedTouches; if our finger lifted, finish.
        const lifted = Array.from(event.changedTouches).some(
          (item) =>
            item.identifier === pointerId ||
            activePointerIdRef.current != null,
        );
        if (!lifted && event.touches.length > 0) return;
        applyPointerUp(pointerId);
      };

      document.addEventListener("pointermove", onMove, {
        capture: true,
        passive: false,
      });
      document.addEventListener("pointerup", onUp, { capture: true });
      document.addEventListener("pointercancel", onPointerCancel, {
        capture: true,
      });
      document.addEventListener("touchmove", onTouchMove, {
        capture: true,
        passive: false,
      });
      document.addEventListener("touchend", onTouchEnd, { capture: true });
      document.addEventListener("touchcancel", onTouchEnd, { capture: true });

      detachDocumentListenersRef.current = () => {
        document.removeEventListener("pointermove", onMove, true);
        document.removeEventListener("pointerup", onUp, true);
        document.removeEventListener("pointercancel", onPointerCancel, true);
        document.removeEventListener("touchmove", onTouchMove, true);
        document.removeEventListener("touchend", onTouchEnd, true);
        document.removeEventListener("touchcancel", onTouchEnd, true);
      };
    },
    [
      applyPointerMove,
      applyPointerUp,
      detachDocumentListeners,
      finishIdle,
      resetDragVisuals,
      setPhaseBoth,
    ],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (disabledRef.current) return;
      if (phaseRef.current === "locked") return;
      if (phaseRef.current !== "idle") return;
      if (event.pointerType === "mouse" && event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();

      activePointerIdRef.current = event.pointerId;
      startPointRef.current = { x: event.clientX, y: event.clientY };
      startedAtRef.current = Date.now();
      cancelledDuringGestureRef.current = false;
      resetDragVisuals();
      setPhaseBoth("holding");

      try {
        (event.currentTarget as HTMLButtonElement).setPointerCapture(
          event.pointerId,
        );
      } catch {
        // ignore — document listeners still cover the gesture
      }

      attachDocumentListeners(event.pointerId);
      void onStartRef.current();
    },
    [attachDocumentListeners, resetDragVisuals, setPhaseBoth],
  );

  // Also support touchstart for environments where pointer events are odd.
  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLButtonElement>) => {
      if (disabledRef.current) return;
      if (phaseRef.current === "locked") return;
      if (phaseRef.current !== "idle") return;
      if (event.touches.length === 0) return;

      // If pointer events already started the gesture, skip.
      if (activePointerIdRef.current != null) return;

      const touch = event.touches[0];
      event.preventDefault();
      event.stopPropagation();

      // Use touch.identifier; document touchmove matches it or falls back.
      activePointerIdRef.current = touch.identifier;
      startPointRef.current = { x: touch.clientX, y: touch.clientY };
      startedAtRef.current = Date.now();
      cancelledDuringGestureRef.current = false;
      resetDragVisuals();
      setPhaseBoth("holding");
      attachDocumentListeners(touch.identifier);
      void onStartRef.current();
    },
    [attachDocumentListeners, resetDragVisuals, setPhaseBoth],
  );

  useEffect(() => {
    return () => {
      detachDocumentListeners();
    };
  }, [detachDocumentListeners]);

  const stopLockedRecording = useCallback(() => {
    if (phaseRef.current !== "locked") return;
    onStopRef.current();
    finishIdle();
  }, [finishIdle]);

  const cancelLockedRecording = useCallback(() => {
    if (phaseRef.current !== "locked") return;
    onCancelRef.current();
    finishIdle();
  }, [finishIdle]);

  const isGestureActive =
    phase === "holding" ||
    phase === "cancelling" ||
    phase === "locking" ||
    phase === "locked";

  return {
    buttonRef,
    phase,
    isGestureActive,
    isLocked: phase === "locked",
    dragOffsetX,
    dragOffsetY,
    lockProgress,
    cancelProgress,
    stopLockedRecording,
    cancelLockedRecording,
    gestureHandlers: {
      onPointerDown: handlePointerDown,
      onTouchStart: handleTouchStart,
    },
  };
};
